import { LitElement, html, css } from 'lit';
import { getConfig } from '../config.js';

/**
 * Composant <ui-number>
 * Entrée numérique avec clavier visuel popover (calculatrice) et adaptation mobile (bottom-sheet)
 */
export class UINumber extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: Number, reflect: true },
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    min: { type: Number },
    max: { type: Number },
    step: { type: Number },
    label: { type: String, reflect: true },
    
    // États internes
    isOpen: { type: Boolean, state: true },
    inputValue: { type: String, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .number-container {
      position: relative;
      width: 100%;
    }

    /* Le Header (Input principal) */
    .number-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .number-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px; /* Espace pour l'icône */
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .number-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .number-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .number-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .number-icon {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      color: var(--ui-text-muted);
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .number-container.open .number-icon {
      color: var(--ui-primary);
    }

    /* Le Clavier Popover */
    .number-panel {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 240px;
      padding: 16px;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
    }

    .number-panel.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* Grille du Clavier */
    .keypad-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-bottom: 12px;
    }

    .key-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: var(--ui-surface-hover);
      color: var(--ui-text);
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
      outline: none;
      user-select: none;
    }

    .key-btn:hover:not(:disabled) {
      background-color: var(--ui-surface-active);
      border-color: var(--ui-border-hover);
      color: var(--ui-primary);
    }

    .key-btn:active:not(:disabled) {
      background-color: var(--ui-border);
    }

    .key-btn.action-key {
      background-color: var(--ui-surface-active);
      color: var(--ui-text-muted);
    }

    .key-btn.action-key:hover:not(:disabled) {
      color: var(--ui-danger);
    }

    /* Boutons de validation */
    .keypad-footer {
      display: grid;
      grid-template-columns: 1fr 2fr;
      gap: 8px;
    }

    .btn-clear {
      height: 38px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: var(--ui-surface-hover);
      color: var(--ui-text-muted);
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-clear:hover {
      background-color: var(--ui-surface-active);
      color: var(--ui-danger);
    }

    .btn-submit {
      height: 38px;
      border: none;
      border-radius: var(--ui-radius-sm);
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-submit:hover {
      background-color: var(--ui-primary-hover);
    }

    /* Backdrop pour version mobile */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    /* Style Responsive Mobile - Plein Écran / Bottom Sheet */
    @media (max-width: 576px) {
      .number-container.open .backdrop {
        display: block;
      }

      .number-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        transform: translateY(100%) scale(1);
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
      }

      .number-container.open .number-panel {
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      /* Barre de tirage en haut de la bottom sheet */
      .number-panel::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -8px auto 16px auto;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = undefined;
    this.placeholder = '0';
    this.disabled = false;
    this.required = false;
    this.min = undefined;
    this.max = undefined;
    this.step = 1;
    this.label = '';
    
    this.isOpen = false;
    this.inputValue = '';
    this._isFirstInputAfterFocus = false;

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      const stringValue = this.value !== undefined && this.value !== null ? String(this.value) : '';
      this.internals.setFormValue(stringValue);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value !== undefined && this.value !== null && !isNaN(this.value);
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez saisir un nombre.', this.shadowRoot.querySelector('.number-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('value')) {
      this.inputValue = this.value !== undefined && !isNaN(this.value) ? String(this.value) : '';
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closePanel();
    }
  }

  _togglePanel() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closePanel();
    } else {
      this._openPanel();
    }
  }

  _openPanel() {
    this.isOpen = true;
    this._isFirstInputAfterFocus = true;
    setTimeout(() => {
      const input = this.shadowRoot.querySelector('.number-input');
      if (input) {
        input.select();
      }
    }, 50);
  }

  _closePanel() {
    this.isOpen = false;
    this._clampAndSet();
  }

  _handleInput(e) {
    this.inputValue = e.target.value;
    this._isFirstInputAfterFocus = false;
  }

  _handleInputClick(e) {
    if (this.disabled) return;
    e.target.select();
    this._isFirstInputAfterFocus = true;
  }

  _handleBlur() {
    // Si l'utilisateur quitte l'input sans le clavier, on applique les limites
    if (!this.isOpen) {
      this._clampAndSet();
    }
  }

  _clampAndSet() {
    const rawVal = this.inputValue.trim();
    if (rawVal === '') {
      this._setValue(undefined);
      return;
    }

    let parsed = parseFloat(rawVal);
    if (isNaN(parsed)) {
      this.inputValue = this.value !== undefined ? String(this.value) : '';
    } else {
      if (this.min !== undefined && parsed < this.min) parsed = this.min;
      if (this.max !== undefined && parsed > this.max) parsed = this.max;
      this._setValue(parsed);
    }
  }

  _setValue(newValue) {
    const oldValue = this.value;
    this.value = newValue;
    this.inputValue = newValue !== undefined ? String(newValue) : '';

    if (oldValue !== newValue) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      this.dispatchEvent(new CustomEvent('ui-change', {
        detail: { value: newValue },
        bubbles: true,
        composed: true
      }));
    }
  }

  /**
   * Gestion des touches du clavier visuel
   */
  _pressKey(key) {
    let current = this.inputValue;

    if (this._isFirstInputAfterFocus) {
      this._isFirstInputAfterFocus = false;
      current = '';
    }

    if (key === 'backspace') {
      this.inputValue = current.slice(0, -1);
    } else if (key === '.') {
      // Évite les points multiples
      if (!current.includes('.')) {
        this.inputValue = current === '' ? '0.' : current + '.';
      }
    } else {
      // Chiffre
      this.inputValue = current + key;
    }
  }

  _clearInput() {
    this.inputValue = '';
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this._closePanel();
    }
  }

  render() {
    const keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', 'backspace'];
    const displayPlaceholder = this.placeholder || '0';

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="number-container ui-component ${this.isOpen ? 'open' : ''}">
        <!-- Soumission automatique gérée via ElementInternals -->

        <!-- Backdrop mobile -->
        <div class="backdrop" @click="${this._closePanel}"></div>

        <!-- En-tête avec input -->
        <div class="number-header">
          <input 
            type="text" 
            inputmode="none"
            class="number-input" part="input" 
            placeholder="${displayPlaceholder}"
            .value="${this.inputValue}"
            ?disabled="${this.disabled}"
            @focus="${this._openPanel}"
            @click="${this._handleInputClick}"
            @input="${this._handleInput}"
            @blur="${this._handleBlur}"
            @keydown="${this._handleKeyDown}"
          >
          <span class="number-icon" @click="${this._togglePanel}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="9" x2="15" y2="9"></line>
              <line x1="9" y1="13" x2="15" y2="13"></line>
              <line x1="9" y1="17" x2="15" y2="17"></line>
              <line x1="12" y1="9" x2="12" y2="17"></line>
            </svg>
          </span>
        </div>

        <!-- Clavier Popover -->
        <div class="number-panel ${this.isOpen ? 'open' : ''}">
          <div class="keypad-grid">
            ${keys.map(key => {
              if (key === 'backspace') {
                return html`
                  <button type="button" class="key-btn action-key" @click="${() => this._pressKey('backspace')}">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
                      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
                      <line x1="18" y1="9" x2="12" y2="15"></line>
                      <line x1="12" y1="9" x2="18" y2="15"></line>
                    </svg>
                  </button>
                `;
              }
              return html`
                <button type="button" class="key-btn" @click="${() => this._pressKey(key)}">${key}</button>
              `;
            })}
          </div>

          <div class="keypad-footer">
            <button type="button" class="btn-clear" @click="${this._clearInput}">Effacer</button>
            <button type="button" class="btn-submit" @click="${this._closePanel}">Valider</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-number')) {
  customElements.define('ui-number', UINumber);
}
